import { useEffect, useState } from 'react';
import { useDispatch } from 'react-redux';
import { useHistory } from 'react-router-dom';
import { compose } from 'redux';

import { saveEnrolmentIdForEnrolmentManagement } from '../../../../actions/hub/hubEnrolmentManagementActions';
import { fetchEnrolments } from '../../../../actions/hub/hubEnrolmentsPageActions';
import { Enrolment } from '../../../../api/types/enrolment';
import withSelectedEnrolment from '../../../../common/hub/NewWithSelectedEnrolment';
import { CancelEntry } from '../../../../components/EnrolmentManagement/CancelEnrolment/CancelEntry';
import { CancelInterrupt } from '../../../../components/EnrolmentManagement/CancelEnrolment/CancelInterrupt';
import { CancelSuccessMessage } from '../../../../components/EnrolmentManagement/CancelEnrolment/CancelSuccessMessage';
import { FormCancelEnrolment } from '../../../../components/EnrolmentManagement/CancelEnrolment/FormCancelEnrolment';
import useShowCancelInterrupt from '../../../../hooks/enrolment/useShowCancelInterrupt';
import { useGetSessionPolicy } from '../../../../hooks/queries/useSessionQuery';
import { isEnrolmentCancelRequested } from '../../../../util/helpers';
import { useSearchParams } from '../../../../util/hooks/useSearchParams';
import { PATH_HUB_CR_CANCEL_ENROLMENT } from '../../../../util/pagePath';
import { TRACKING_CE } from '../../../../util/trackingClasses';
import {
  SUBSCRIPTION_LOCAL_STORAGE_KEYS,
  SubscriptionLocalStorage,
} from '../../Subscriptions/SubscriptionLocalStorage';

const CANCEL_FLOW_STEPS = {
  ENTRY: 'ENTRY',
  FORM: 'FORM',
  CONFIRMED: 'CONFIRMED',
  INTERRUPT: 'INTERRUPT',
};

interface Props {
  selectedEnrolment: Enrolment;
}

export const CancelEnrolment = ({ selectedEnrolment }: Props) => {
  const dispatch = useDispatch();
  const history = useHistory();

  const {
    enrolmentStatus,
    enrolmentSfid,
    enrolmentCancelRequestedDate,
    loyaltyVoucherIssued,
    numOfLoyaltyQualifiedSessions,
  } = selectedEnrolment;

  const {
    data: enrolmentPolicy,
    isLoading: pausePolicyLoading,
    error: policyError,
  } = useGetSessionPolicy({
    enrolmentId: enrolmentSfid,
  });

  const { showCancelInterrupt, currentPricingPlan } = useShowCancelInterrupt({
    loyaltyVoucherIssued,
    numOfLoyaltyQualifiedSessions,
  });

  const isCancelRequested = isEnrolmentCancelRequested({ enrolmentStatus });
  const query = useSearchParams();
  const goToCancelForm = query.get('view') === 'form';

  const [cancelRequestedDate, setCancelRequestedDate] = useState<string | null>(enrolmentCancelRequestedDate ?? null);

  let defaultView = CANCEL_FLOW_STEPS.ENTRY;
  if (isCancelRequested) {
    defaultView = CANCEL_FLOW_STEPS.CONFIRMED;
  } else if (goToCancelForm) {
    defaultView = showCancelInterrupt ? CANCEL_FLOW_STEPS.INTERRUPT : CANCEL_FLOW_STEPS.FORM;
  } else {
    defaultView = CANCEL_FLOW_STEPS.ENTRY;
  }

  const [activeView, setActiveView] = useState(defaultView);
  const [previousView, setPreviousView] = useState(null);

  useEffect(() => {
    setActiveView(defaultView);
    setPreviousView(null);
  }, [defaultView]);

  // Handle clicking `Continue to learning` button in the cancel interrupt page
  // Will return to the form entry step if from entry step, otherwise will return to the previous page
  const goToPreviousView = () => {
    if (previousView) {
      setActiveView(previousView);
      setPreviousView(null);
      return;
    }

    if (goToCancelForm) {
      history.goBack();
      return;
    }

    setActiveView(CANCEL_FLOW_STEPS.ENTRY);
  };

  const cancelEntryCheck = () => {
    setPreviousView(CANCEL_FLOW_STEPS.ENTRY);
    if (showCancelInterrupt) {
      setActiveView(CANCEL_FLOW_STEPS.INTERRUPT);
    } else {
      setActiveView(CANCEL_FLOW_STEPS.FORM);
    }
  };

  switch (activeView) {
    case CANCEL_FLOW_STEPS.CONFIRMED:
      return (
        <CancelSuccessMessage
          selectedEnrolment={selectedEnrolment}
          tracking={TRACKING_CE.SUCCESS}
          cancelRequestedDate={cancelRequestedDate}
          cleanUp={
            isCancelRequested
              ? () => {}
              : () => {
                  dispatch(fetchEnrolments('active'));
                  dispatch(saveEnrolmentIdForEnrolmentManagement({ enrolmentId: '' }));
                }
          }
        />
      );
    case CANCEL_FLOW_STEPS.FORM:
      return (
        <FormCancelEnrolment
          selectedEnrolment={selectedEnrolment}
          goBack={() => {
            setActiveView(CANCEL_FLOW_STEPS.ENTRY);
          }}
          submitSuccessCallback={() => {
            setActiveView(CANCEL_FLOW_STEPS.CONFIRMED);
            // Add the enrolment id to the subscriptionlocal storage to track the enrolment that is being cancelled
            SubscriptionLocalStorage.set(
              SUBSCRIPTION_LOCAL_STORAGE_KEYS.CLUEY_PLUS_CANCEL_ENROLMENT_REQUESTED,
              selectedEnrolment?.enrolmentId
            );
          }}
          enrolmentPolicyError={policyError}
          setCancelRequestedDate={setCancelRequestedDate}
        />
      );
    case CANCEL_FLOW_STEPS.INTERRUPT:
      if (!currentPricingPlan) {
        return null;
      }

      return (
        <CancelInterrupt
          enrolment={selectedEnrolment}
          pricingPlan={currentPricingPlan}
          goBack={goToPreviousView}
          continueCancel={() => {
            setActiveView(CANCEL_FLOW_STEPS.FORM);
          }}
        />
      );
    default:
      return (
        <CancelEntry
          selectedEnrolment={selectedEnrolment}
          switchToForm={cancelEntryCheck}
          pausePolicyLoading={pausePolicyLoading}
          enrolmentPolicy={enrolmentPolicy}
          enrolmentPolicyError={policyError}
        />
      );
  }
};

export default compose(withSelectedEnrolment(CancelEnrolment, null, PATH_HUB_CR_CANCEL_ENROLMENT));
